import { useMemo, useRef, useState } from 'react';

import type { AmountInputProps } from '../../index.types';

type Result = ModelHookResult<
	{
		inputRef: React.RefObject<HTMLInputElement | null>;
		showOther: boolean;
		inputWidthCh: number;
		applySelectedStyle: boolean;
		valueInDecimal: string;
	},
	{
		handleActivate: () => void;
		handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
		handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
		handleBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	}
>;

type Props = Pick<AmountInputProps, 'onChange' | 'onFocus' | 'onBlur' | 'selected' | 'error'>;

export function useAmountInput({ onChange, onFocus, onBlur, selected, error }: Props): Result {
	const inputRef = useRef<HTMLInputElement>(null);
	const [focused, setFocused] = useState(false);
	const [raw, setRaw] = useState('');

	const handleActivate = () => {
		if (!focused) {
			setFocused(true);
			onChange(raw);
			onFocus();
			queueMicrotask(() => inputRef.current?.focus());
		}
	};

	const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		const digits = e.target.value.replace(/\D/g, '');
		setRaw(digits);
		onChange(digits);
	};
	const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
		if (e.key === '.' || e.key === ',' || e.key === 'Enter') {
			e.preventDefault();
			if (e.key === 'Enter') inputRef.current?.blur();
		}
	};
	const handleBlur: React.FocusEventHandler<HTMLInputElement> = () => {
		setFocused(false);
		onBlur?.();
	};

	const valueInDecimal = raw === '' ? '' : String(parseInt(raw, 10));
	// Dynamic width in ch units; at least 1ch so caret is visible
	const inputWidthCh = valueInDecimal.length || 1;

	const applySelectedStyle = useMemo(() => {
		return !!(selected && !error && !focused);
	}, [selected, error, focused]);

	return {
		data: {
			inputRef,
			showOther: !focused && raw === '',
			inputWidthCh,
			applySelectedStyle,
			valueInDecimal,
		},
		actions: {
			handleActivate,
			handleChange,
			handleKeyDown,
			handleBlur,
		},
	};
}
